/**
 * OCR ResultCanvas — tabbed reader (Extracted / Summary / Smart-action outputs).
 * Extracted from OCRScanner.jsx (was the largest sub-tree by line count).
 */
import { Copy, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import Markdown from "@/components/Markdown";
import ExportMenu from "@/components/ExportMenu";
import { Skeleton } from "@/components/Skeleton";

export default function ResultCanvas({
  result,
  title,
  smartActions,
  activeTab,
  setActiveTab,
  actionOutputs,
  actionLoading,
  onCopy,
}) {
  const tabs = [
    { key: "extract", label: "Extracted" },
    { key: "summary", label: "Summary" },
    ...smartActions.filter((a) => actionOutputs[a.key]).map((a) => ({ key: a.key, label: a.label })),
  ];

  const exportTitle = activeTab === "extract" ? (title || "Extracted text")
    : activeTab === "summary" ? `${title || "Document"} · Summary`
    : (actionOutputs[activeTab]?.title || activeTab);

  const exportContent = activeTab === "extract" ? `# ${title || "Extracted text"}\n\n\`\`\`\n${result.extracted_text}\n\`\`\``
    : activeTab === "summary" ? result.summary
    : (actionOutputs[activeTab]?.content || "");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="tb-chip-mint rounded-full text-[11px] px-2.5 py-1 font-medium">{result.detected_language}</span>
          <span className="tb-chip-lavender rounded-full text-[11px] px-2.5 py-1 font-medium">TeeBee Vision OCR</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={onCopy} variant="outline" size="sm" className="rounded-full" data-testid="result-copy">
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
          </Button>
          <ExportMenu title={exportTitle} content={exportContent} includePptx={false} />
        </div>
      </div>

      {/* Tab pills */}
      <div className="flex gap-1 overflow-x-auto tb-no-scrollbar pb-1 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`shrink-0 px-3.5 py-2 text-sm rounded-t-lg -mb-px border-b-2 transition-colors ${
              activeTab === t.key
                ? "border-primary text-foreground font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
        {actionLoading && (
          <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm text-primary">
            <Loader2 className="h-3 w-3 animate-spin" /> Generating…
          </span>
        )}
      </div>

      {/* Tab content */}
      <div className="tb-fade-up">
        {activeTab === "extract" && (
          <Textarea
            className="rounded-xl font-mono text-xs"
            rows={12}
            readOnly
            value={result.extracted_text}
            data-testid="ocr-text-output"
          />
        )}
        {activeTab === "summary" && <Markdown>{result.summary}</Markdown>}
        {activeTab !== "extract" && activeTab !== "summary" && (
          actionOutputs[activeTab] ? (
            <Markdown>{actionOutputs[activeTab].content}</Markdown>
          ) : actionLoading === activeTab ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Tap the action again to generate.</p>
          )
        )}
      </div>
    </div>
  );
}
